import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  LuActivity,
  LuFilter,
  LuRefreshCw,
  LuCircleCheck,
  LuTrash2,
  LuTriangleAlert,
  LuInfo,
  LuShieldAlert,
  LuFlame,
} from "react-icons/lu";
import type { ComponentType } from "react";
import { api, ApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { SkeletonList } from "@/components/ui/skeleton";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

type Severity = "info" | "warning" | "error" | "critical";

const SEVERITY_META: Record<
  Severity,
  { icon: ComponentType<{ className?: string }>; chip: string; row: string }
> = {
  info: {
    icon: LuInfo,
    chip: "bg-[var(--color-elevated)] border-[var(--color-border)] text-[var(--color-text-muted)]",
    row: "",
  },
  warning: {
    icon: LuTriangleAlert,
    chip: "bg-[rgba(250,176,5,0.1)] border-[rgba(250,176,5,0.3)] text-[#fcd34d]",
    row: "border-l-2 border-l-[#fcd34d]/40",
  },
  error: {
    icon: LuShieldAlert,
    chip: "bg-[rgba(237,66,69,0.1)] border-[rgba(237,66,69,0.3)] text-[#fca5a5]",
    row: "border-l-2 border-l-[#fca5a5]/40",
  },
  critical: {
    icon: LuFlame,
    chip: "bg-[rgba(237,66,69,0.18)] border-[rgba(237,66,69,0.5)] text-[#ef4444]",
    row: "border-l-2 border-l-[#ef4444]",
  },
};

export function EventsPage() {
  const qc = useQueryClient();
  const [severity, setSeverity] = useState<"" | Severity>("");
  const [status, setStatus] = useState<"all" | "open" | "resolved">("open");
  const [kind, setKind] = useState("");
  const [purgeOpen, setPurgeOpen] = useState(false);

  const eventsQuery = useQuery({
    queryKey: ["console", "events", { severity, status, kind }],
    queryFn: () =>
      api.events.list({
        limit: 200,
        severity: severity || undefined,
        status,
        kind: kind || undefined,
      }),
    refetchInterval: 60_000,
  });

  const resolveMut = useMutation({
    mutationFn: (id: string) => api.events.resolve(id),
    onSuccess: () => {
      toast.success("Event resolved");
      qc.invalidateQueries({ queryKey: ["console", "events"] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "Resolve failed"),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.events.delete(id),
    onSuccess: () => {
      toast.success("Event deleted");
      qc.invalidateQueries({ queryKey: ["console", "events"] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "Delete failed"),
  });

  const purgeMut = useMutation({
    mutationFn: () => api.events.bulkDelete({ status: "resolved" }),
    onSuccess: (res) => {
      toast.success(`Removed ${res.deleted.toLocaleString()} resolved events.`);
      setPurgeOpen(false);
      qc.invalidateQueries({ queryKey: ["console", "events"] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "Purge failed"),
  });

  const data = eventsQuery.data;
  const totalSeverity = data?.breakdown.bySeverity ?? {};

  return (
    <>
      <PageHeader
        icon={LuActivity}
        title="Events"
        description="Signals from cron scanners, alarms, and any worker on this account that posts to /events/ingest."
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => eventsQuery.refetch()}
              disabled={eventsQuery.isFetching}
            >
              <LuRefreshCw className={`w-4 h-4 ${eventsQuery.isFetching ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button variant="danger" onClick={() => setPurgeOpen(true)}>
              <LuTrash2 className="w-4 h-4" />
              Purge resolved
            </Button>
          </>
        }
      />

      <div className="p-8 space-y-6">
        {/* Stat row */}
        {data && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Open" value={data.breakdown.open.toLocaleString()} tone="violet" />
            <Stat
              label="Critical"
              value={(totalSeverity.critical ?? 0).toLocaleString()}
              tone="red"
            />
            <Stat
              label="Warning"
              value={(totalSeverity.warning ?? 0).toLocaleString()}
              tone="amber"
            />
            <Stat
              label="Total"
              value={(data.pagination.total).toLocaleString()}
              tone="cyan"
            />
          </div>
        )}

        {/* Filters */}
        <Card>
          <div className="px-5 py-4 border-b border-[var(--color-border)] flex items-center gap-2">
            <LuFilter className="w-4 h-4 text-[var(--color-text-subtle)]" />
            <p className="text-sm font-semibold text-[var(--color-text)]">Filters</p>
          </div>
          <div className="p-5 flex flex-wrap items-end gap-3">
            <FilterSelect
              label="Status"
              value={status}
              onChange={(v) => setStatus(v as typeof status)}
              options={[
                { value: "open", label: "Open only" },
                { value: "resolved", label: "Resolved only" },
                { value: "all", label: "All" },
              ]}
            />
            <FilterSelect
              label="Severity"
              value={severity}
              onChange={(v) => setSeverity(v as "" | Severity)}
              options={[
                { value: "", label: "Any" },
                { value: "info", label: "Info" },
                { value: "warning", label: "Warning" },
                { value: "error", label: "Error" },
                { value: "critical", label: "Critical" },
              ]}
            />
            <div className="flex flex-wrap gap-2 ml-auto">
              {data?.breakdown.byKind.slice(0, 6).map((k) => (
                <button
                  key={k.kind}
                  type="button"
                  onClick={() => setKind(kind === k.kind ? "" : k.kind)}
                  className={`text-[11px] px-2 py-1 rounded-md border transition-colors ${
                    kind === k.kind
                      ? "bg-[rgba(139,92,246,0.12)] border-[rgba(139,92,246,0.4)] text-[var(--color-accent-light)]"
                      : "bg-[var(--color-bg)] border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                  }`}
                >
                  {k.kind}
                  <span className="ml-1.5 text-[10px] tabular-nums opacity-70">
                    {k.count}
                  </span>
                </button>
              ))}
              {kind && (
                <button
                  type="button"
                  onClick={() => setKind("")}
                  className="text-[11px] text-[var(--color-text-subtle)] hover:text-[var(--color-text)] underline"
                >
                  clear kind
                </button>
              )}
            </div>
          </div>
        </Card>

        {/* List */}
        {eventsQuery.isLoading && (
          <SkeletonList />
        )}
        {!eventsQuery.isLoading && (
        <Card>

          {data && data.events.length === 0 && (
            <div className="px-5 py-12 text-center">
              <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-[var(--color-elevated)] flex items-center justify-center">
                <LuActivity className="w-5 h-5 text-[var(--color-text-subtle)]" />
              </div>
              <p className="text-sm text-[var(--color-text-muted)]">
                No events match. Scanners post here via{" "}
                <code className="text-xs">POST /events/ingest</code>.
              </p>
            </div>
          )}

          <div className="divide-y divide-[var(--color-border)]">
            {data?.events.map((e) => {
              const meta = SEVERITY_META[e.severity];
              const Icon = meta.icon;
              return (
                <div
                  key={e.id}
                  className={`px-5 py-4 hover:bg-[var(--color-elevated)]/30 transition-colors ${meta.row}`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`w-7 h-7 rounded-md flex items-center justify-center border shrink-0 ${meta.chip}`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-sm font-medium text-[var(--color-text)]">
                          {e.title}
                        </span>
                        <span
                          className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${meta.chip}`}
                        >
                          {e.severity}
                        </span>
                        <span className="text-[11px] text-[var(--color-text-subtle)] font-mono">
                          {e.source} · {e.kind}
                        </span>
                        {e.resolvedAt && (
                          <span className="text-[10px] text-[#6ee7b7] flex items-center gap-1">
                            <LuCircleCheck className="w-3 h-3" />
                            resolved
                          </span>
                        )}
                      </div>
                      {e.message && (
                        <p className="text-xs text-[var(--color-text-muted)] mt-1 leading-relaxed">
                          {e.message}
                        </p>
                      )}
                      {e.targetKind && (
                        <p className="text-[11px] text-[var(--color-text-subtle)] font-mono mt-1">
                          {e.targetKind}: {e.targetId}
                        </p>
                      )}
                      {e.payload && (
                        <details className="mt-2">
                          <summary className="text-[11px] text-[var(--color-text-subtle)] hover:text-[var(--color-text-muted)] cursor-pointer">
                            payload
                          </summary>
                          <pre className="mt-1 text-[11px] font-mono bg-[var(--color-bg)] border border-[var(--color-border)] rounded-md p-2 overflow-x-auto">
                            {tryFormat(e.payload)}
                          </pre>
                        </details>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0 text-[11px] text-[var(--color-text-subtle)] font-mono tabular-nums">
                      <span>{new Date(e.createdAt).toLocaleString()}</span>
                      <div className="flex items-center gap-1 text-xs">
                        {!e.resolvedAt && (
                          <button
                            type="button"
                            onClick={() => resolveMut.mutate(e.id)}
                            disabled={resolveMut.isPending}
                            className="text-[var(--color-text-muted)] hover:text-[#6ee7b7] transition-colors p-1"
                            title="Resolve"
                          >
                            <LuCircleCheck className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => deleteMut.mutate(e.id)}
                          disabled={deleteMut.isPending}
                          className="text-[var(--color-text-muted)] hover:text-[var(--color-error)] transition-colors p-1"
                          title="Delete"
                        >
                          <LuTrash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
        )}

        {/* Ingest hint */}
        <Card>
          <div className="p-5 text-xs text-[var(--color-text-muted)] space-y-2">
            <p className="text-sm font-semibold text-[var(--color-text)]">
              Push from a worker
            </p>
            <p>
              Any worker on this account can drop a row by posting to{" "}
              <code className="text-[var(--color-accent-light)]">/events/ingest</code>{" "}
              with the shared <code>CONSOLE_INTERNAL_TOKEN</code>:
            </p>
            <pre className="font-mono text-[11px] bg-[var(--color-bg)] border border-[var(--color-border)] rounded-md p-3 overflow-x-auto">
{`fetch("https://console-api.destroy.network/events/ingest", {
  method: "POST",
  headers: {
    "X-Internal-Token": env.CONSOLE_INTERNAL_TOKEN,
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    source: "dnsbl-scanner",
    kind: "domain.blacklisted",
    severity: "warning",
    title: "748392.best listed on Spamhaus ZEN",
    targetKind: "domain",
    targetId: "748392.best",
    payload: { provider: "Spamhaus ZEN", listedAt: "..." },
  }),
});`}
            </pre>
          </div>
        </Card>
      </div>

      <ConfirmDialog
        open={purgeOpen}
        title="Delete all resolved events?"
        description="Removes every event that has been marked resolved. Open events are untouched."
        confirmLabel="Purge"
        destructive
        pending={purgeMut.isPending}
        onClose={() => !purgeMut.isPending && setPurgeOpen(false)}
        onConfirm={() => purgeMut.mutate()}
      />
    </>
  );
}

function tryFormat(s: string): string {
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return s;
  }
}

const TONE: Record<string, { bg: string; border: string; text: string }> = {
  violet: {
    bg: "bg-[rgba(139,92,246,0.1)]",
    border: "border-[rgba(139,92,246,0.25)]",
    text: "text-[var(--color-accent-light)]",
  },
  cyan: {
    bg: "bg-[rgba(65,209,255,0.08)]",
    border: "border-[rgba(65,209,255,0.25)]",
    text: "text-[#41d1ff]",
  },
  red: {
    bg: "bg-[rgba(237,66,69,0.08)]",
    border: "border-[rgba(237,66,69,0.25)]",
    text: "text-[#fca5a5]",
  },
  amber: {
    bg: "bg-[rgba(250,176,5,0.08)]",
    border: "border-[rgba(250,176,5,0.25)]",
    text: "text-[#fcd34d]",
  },
};

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: keyof typeof TONE;
}) {
  const t = TONE[tone];
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-[0.12em] text-[var(--color-text-subtle)]">
          {label}
        </p>
        <span className={`w-1.5 h-1.5 rounded-full ${t.bg.replace("bg-", "bg-")} ${t.text}`} />
      </div>
      <p className={`text-xl font-semibold mt-2 tabular-nums ${t.text}`}>{value}</p>
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.12em] text-[var(--color-text-subtle)] mb-2">
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="px-3 py-2 text-sm rounded-md bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)] focus:outline-none focus:border-[var(--color-accent)]"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}
